/**
 * SwhSeriesEditor — shape: swh_series (SOLAR_WATER_HEATER).
 * Edits the shared `spec` PLUS a per-series variants array; each series carries
 * the 13-parameter `spec13` object + tdsLimit / airVentLimit / operatingMode /
 * docRef / sourceFlag. Series picker on the left, the selected series on the right.
 */

import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  CategorySpecEditor,
  FieldLabel,
  SpecInput,
  MiniLabel,
  iconBtnStyle,
  inputStyle,
  type CategorySpecShape,
} from './specPrimitives';

const SPEC13_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'innerTankMaterial', label: 'Inner Tank Material' },
  { key: 'innerTankSheetThickness', label: 'Inner Tank Sheet Thickness' },
  { key: 'innerTankDishThickness', label: 'Inner Tank Dish Thickness' },
  { key: 'sacrificialProtection', label: 'Sacrificial Protection' },
  { key: 'pufInsulation', label: 'PUF Insulation' },
  { key: 'outerCladding', label: 'Outer Cladding' },
  { key: 'supportingStructure', label: 'Supporting Structure' },
  { key: 'solarCollector', label: 'Solar Collector' },
  { key: 'tubeDiaLength', label: 'Tube Dia × Length' },
  { key: 'glassMaterial', label: 'Glass Material' },
  { key: 'selectiveCoating', label: 'Selective Coating' },
  { key: 'absorptance', label: 'Absorptance' },
  { key: 'stagnationTemperature', label: 'Stagnation Temperature' },
  { key: 'referenceDrawing', label: 'Reference Drawing' },
];

interface SwhVariant {
  series: string;
  marketingName: string;
  tagline: string;
  spec13: Record<string, string>;
  tdsLimit: string;
  airVentLimit: string;
  operatingMode: string;
  docRef: string;
  sourceFlag: string;
}

export function SwhSeriesEditor({
  data,
  tokens,
  onChange,
}: {
  data: any;
  tokens: string[];
  onChange: (next: any) => void;
}) {
  const spec: CategorySpecShape = data?.spec ?? { title: '', kpiTiles: [], specRows: [] };
  const variants: SwhVariant[] = Array.isArray(data?.variants) ? data.variants : [];

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (idx >= variants.length) setIdx(Math.max(0, variants.length - 1));
  }, [variants.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSpec = (next: CategorySpecShape) => onChange({ ...data, spec: next });
  const setVariants = (next: SwhVariant[]) => onChange({ ...data, variants: next });

  const patch = (i: number, p: Partial<SwhVariant>) =>
    setVariants(variants.map((v, j) => (j === i ? { ...v, ...p } : v)));
  const patch13 = (i: number, key: string, val: string) =>
    setVariants(variants.map((v, j) => (j === i ? { ...v, spec13: { ...(v.spec13 ?? {}), [key]: val } } : v)));
  const remove = (i: number) => setVariants(variants.filter((_, j) => j !== i));
  const add = () => {
    setVariants([
      ...variants,
      {
        series: 'NEW',
        marketingName: '',
        tagline: '',
        spec13: Object.fromEntries(SPEC13_FIELDS.map((f) => [f.key, ''])),
        tdsLimit: '',
        airVentLimit: '',
        operatingMode: '',
        docRef: '',
        sourceFlag: 'DERIVED',
      },
    ]);
    setIdx(variants.length);
  };

  const current = variants[idx];

  return (
    <>
      <CategorySpecEditor spec={spec} tokens={tokens} onChange={setSpec} />

      <FieldLabel label="Series" hint="each series carries the 13-parameter spec">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {variants.map((v, i) => {
            const on = i === idx;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 30,
                  padding: '0 12px',
                  borderRadius: 8,
                  fontSize: 12.5,
                  fontWeight: on ? 700 : 500,
                  border: `1px solid ${on ? 'var(--ink-0)' : 'var(--line)'}`,
                  background: on ? 'var(--ink-0)' : 'var(--ivory-0)',
                  color: on ? 'var(--ivory-0)' : 'var(--ink-1)',
                  cursor: 'pointer',
                }}
              >
                {v.series || `Series ${i + 1}`}
              </button>
            );
          })}
          <button
            type="button"
            onClick={add}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              height: 30,
              padding: '0 12px',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 600,
              border: '1px dashed var(--line-strong)',
              background: 'var(--ivory-0)',
              color: 'var(--ink-2)',
              cursor: 'pointer',
            }}
          >
            <Plus size={13} /> Add series
          </button>
        </div>

        {current && (
          <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 12, background: 'var(--ivory-0)' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Remove series "${current.series}"?`)) remove(idx);
                }}
                title="Remove this series"
                style={iconBtnStyle('#b91c1c')}
              >
                <Trash2 size={13} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <div>
                <MiniLabel>Series key</MiniLabel>
                <SpecInput value={current.series ?? ''} onChange={(v) => patch(idx, { series: v })} placeholder="e.g. WB" />
              </div>
              <div>
                <MiniLabel>Marketing name</MiniLabel>
                <SpecInput value={current.marketingName ?? ''} onChange={(v) => patch(idx, { marketingName: v })} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <MiniLabel>Tagline</MiniLabel>
                <SpecInput value={current.tagline ?? ''} onChange={(v) => patch(idx, { tagline: v })} />
              </div>
            </div>

            <MiniLabel>13-parameter spec</MiniLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {SPEC13_FIELDS.map((f) => (
                <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: '40%', fontSize: 12, color: 'var(--ink-2)' }}>{f.label}</div>
                  <div style={{ flex: 1 }}>
                    <SpecInput
                      value={current.spec13?.[f.key] ?? ''}
                      onChange={(v) => patch13(idx, f.key, v)}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <MiniLabel>TDS limit</MiniLabel>
                <SpecInput value={current.tdsLimit ?? ''} onChange={(v) => patch(idx, { tdsLimit: v })} />
              </div>
              <div>
                <MiniLabel>Air-vent limit</MiniLabel>
                <SpecInput value={current.airVentLimit ?? ''} onChange={(v) => patch(idx, { airVentLimit: v })} />
              </div>
              <div>
                <MiniLabel>Operating mode</MiniLabel>
                <SpecInput value={current.operatingMode ?? ''} onChange={(v) => patch(idx, { operatingMode: v })} />
              </div>
              <div>
                <MiniLabel>Doc ref</MiniLabel>
                <SpecInput value={current.docRef ?? ''} onChange={(v) => patch(idx, { docRef: v })} />
              </div>
              <div>
                <MiniLabel>Source flag</MiniLabel>
                <select
                  value={current.sourceFlag ?? 'DERIVED'}
                  onChange={(e) => patch(idx, { sourceFlag: e.target.value })}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="VERBATIM">VERBATIM</option>
                  <option value="DERIVED">DERIVED</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </FieldLabel>
    </>
  );
}
